/**
 * Utility functions for filtering games based on various criteria
 */
import { filterGamesByTeamSearch } from './searchUtils';

// Helper functions to reduce cognitive complexity
const passesTeamFilters = (game, filters) => {
  if (filters.top25 && !hasRankedTeam(game)) return false;
  if (filters.rankedMatchups === true && !isRankedMatchup(game)) return false;
  if (filters.fbs === true && !isFBSGame(game)) return false;
  if (filters.fcs === true && !isFCSGame(game)) return false;
  return true;
};

const passesConferenceFilters = (game, filters) => {
  // Handle conference type filter (All | Conf | Non-Conf)
  if (filters.conferenceType && filters.conferenceType !== 'all') {
    const homeConference = game.home_team?.conference;
    const awayConference = game.away_team?.conference;
    const isConferenceGame = homeConference === awayConference;

    if (filters.conferenceType === 'conference' && !isConferenceGame) return false;
    if (filters.conferenceType === 'non-conf' && isConferenceGame) return false;
  }

  // Handle specific conference selection
  if (filters.conferences && filters.conferences.length > 0) {
    // For OR logic, check if both teams are in ANY of the selected conferences
    if (filters.filterLogic === 'OR') {
      return filters.conferences.some(conference => {
        const homeConference = game.home_team?.conference;
        const awayConference = game.away_team?.conference;
        return homeConference === conference && awayConference === conference;
      });
    } else {
      // For AND logic, use the existing hasMatchingConference function
      return hasMatchingConference(game, filters.conferences);
    }
  }
  return true;
};

const passesBettingFilters = (game, filters) => {
  if (filters.homeUnderdogs === true && !isHomeUnderdog(game)) return false;
  if (filters.atsLockAvailable === true && !hasAtsLockAvailable(game)) return false;
  if (filters.overUnderLockAvailable === true && !hasOverUnderLockAvailable(game)) return false;
  if (filters.spreadMin !== null && filters.spreadMin !== undefined && !meetsSpreadMin(game, filters.spreadMin)) return false;
  if (filters.spreadMax !== null && filters.spreadMax !== undefined && !meetsSpreadMax(game, filters.spreadMax)) return false;
  if (filters.overUnderMin !== null && filters.overUnderMin !== undefined && !meetsOverUnderMin(game, filters.overUnderMin)) return false;
  if (filters.overUnderMax !== null && filters.overUnderMax !== undefined && !meetsOverUnderMax(game, filters.overUnderMax)) return false;
  return true;
};

const passesGameTypeFilters = (game, filters) => {
  return !(filters.neutralSite === true && !isNeutralSite(game));
};

const passesAdvancedFilters = (game, filters) => {
  // Smart filters
  if (filters.closeMatchups === true && !isCloseMatchup(game)) return false;
  if (filters.mismatches === true && !isMismatch(game)) return false;
  if (filters.barnBurners === true && !isBarnBurner(game)) return false;

  // Custom range filters
  if (filters.excitementIndexMin !== null && filters.excitementIndexMin !== undefined && !meetsExcitementIndexMin(game, filters.excitementIndexMin)) return false;
  if (filters.excitementIndexMax !== null && filters.excitementIndexMax !== undefined && !meetsExcitementIndexMax(game, filters.excitementIndexMax)) return false;
  if (filters.eloDifferenceMin !== null && filters.eloDifferenceMin !== undefined && !meetsEloDifferenceMin(game, filters.eloDifferenceMin)) return false;
  if (filters.eloDifferenceMax !== null && filters.eloDifferenceMax !== undefined && !meetsEloDifferenceMax(game, filters.eloDifferenceMax)) return false;
  return true;
};

/**
 * Apply all active filters to a list of games
 * @param {Array} games - Array of game objects
 * @param {Object} filters - Filter configuration object
 * @returns {Array} Filtered array of games
 */
export const applyFilters = (games, filters) => {
  if (!games || !filters) return games;

  // Apply team search filter first if present
  let filteredGames = games;
  if (filters.teamSearch && filters.teamSearch.trim() !== '') {
    filteredGames = filterGamesByTeamSearch(games, filters.teamSearch);
  }

  const result = filteredGames.filter(game => {
    // Get individual filter results
    const teamFiltersPass = passesTeamFilters(game, filters);
    const conferenceFiltersPass = passesConferenceFilters(game, filters);
    const bettingFiltersPass = passesBettingFilters(game, filters);
    const gameTypeFiltersPass = passesGameTypeFilters(game, filters);
    const advancedFiltersPass = passesAdvancedFilters(game, filters);

    // Apply logic based on filterLogic setting
    // Note: OR logic for conferences is handled within passesConferenceFilters
    // Both OR and AND logic return the same result since OR logic is handled within passesConferenceFilters
    return teamFiltersPass && conferenceFiltersPass && bettingFiltersPass && gameTypeFiltersPass && advancedFiltersPass;
  });

  return result;
};

/**
 * Count the number of games that would be displayed with current filters
 * @param {Array} games - Array of game objects
 * @param {Object} filters - Filter configuration object
 * @returns {number} Number of games that pass all filters
 */
export const getDisplayedGamesCount = (games, filters) => {
  if (!games || !filters) return 0;
  return applyFilters(games, filters).length;
};

/**
 * Check if game has at least one ranked team
 */
const hasRankedTeam = (game) => {
  const awayRank = game.away_team?.rank;
  const homeRank = game.home_team?.rank;

  // Explicitly check for null/undefined and ensure it's a number
  const awayRankValid = awayRank !== null && awayRank !== undefined && typeof awayRank === 'number' && awayRank <= 25;
  const homeRankValid = homeRank !== null && homeRank !== undefined && typeof homeRank === 'number' && homeRank <= 25;

  const hasRanked = awayRankValid || homeRankValid;



  return hasRanked;
};

/**
 * Check if both teams are ranked
 */
const isRankedMatchup = (game) => {
  const awayRank = game.away_team?.rank;
  const homeRank = game.home_team?.rank;

  // Explicitly check for null/undefined and ensure it's a number
  const awayRankValid = awayRank !== null && awayRank !== undefined && typeof awayRank === 'number' && awayRank <= 25;
  const homeRankValid = homeRank !== null && homeRank !== undefined && typeof homeRank === 'number' && homeRank <= 25;

  const isRanked = awayRankValid && homeRankValid;

  // Debug logging for first few games
  if (game.id <= 3) {
    console.log('isRankedMatchup check:', {
      gameId: game.id,
      matchup: `${game.away_team.name} @ ${game.home_team.name}`,
      awayRank: awayRank,
      homeRank: homeRank,
      awayRankType: typeof awayRank,
      homeRankType: typeof homeRank,
      awayRankValid: awayRankValid,
      homeRankValid: homeRankValid,
      result: isRanked
    });
  }

  return isRanked;
};

/**
 * Check if game involves at least one FBS team
 */
const isFBSGame = (game) => {
  return game.home_team?.classification === 'fbs' ||
         game.away_team?.classification === 'fbs';
};

/**
 * Check if game involves only FCS teams
 */
const isFCSGame = (game) => {
  return game.home_team?.classification === 'fcs' &&
         game.away_team?.classification === 'fcs';
};

/**
 * Check if game involves teams from selected conferences
 */
const hasMatchingConference = (game, selectedConferences) => {
  if (!selectedConferences || selectedConferences.length === 0) return true;

  const homeConference = game.home_team?.conference;
  const awayConference = game.away_team?.conference;

  // Both teams must be in the same conference, and that conference must be in the selected list
  return homeConference === awayConference && selectedConferences.includes(homeConference);
};

/**
 * Check if home team is an underdog (positive spread)
 */
const isHomeUnderdog = (game) => {
  const spread = game.betting?.spread;
  return spread !== null && spread > 0;
};

/**
 * Check if spread meets minimum threshold
 */
const meetsSpreadMin = (game, minSpread) => {
  const spread = Math.abs(game.betting?.spread || 0);
  return spread >= minSpread;
};

/**
 * Check if spread meets maximum threshold
 */
const meetsSpreadMax = (game, maxSpread) => {
  const spread = Math.abs(game.betting?.spread || 0);
  return spread <= maxSpread;
};

/**
 * Check if over/under meets minimum threshold
 */
const meetsOverUnderMin = (game, minTotal) => {
  const total = game.betting?.over_under;
  return total !== null && total >= minTotal;
};

/**
 * Check if over/under meets maximum threshold
 */
const meetsOverUnderMax = (game, maxTotal) => {
  const total = game.betting?.over_under;
  return total !== null && total <= maxTotal;
};

/**
 * Check if ATS lock is available (spread exists and is not N/A)
 */
const hasAtsLockAvailable = (game) => {
  const spread = game.betting?.spread;
  return spread !== null && spread !== undefined && spread !== 'N/A';
};

/**
 * Check if Over/Under lock is available (over_under exists and is not N/A)
 */
const hasOverUnderLockAvailable = (game) => {
  const overUnder = game.betting?.over_under;
  return overUnder !== null && overUnder !== undefined && overUnder !== 'N/A';
};

/**
 * Check if game is a close matchup (ELO difference ≤ 50)
 */
const isCloseMatchup = (game) => {
  const homeElo = game.home_pregame_elo;
  const awayElo = game.away_pregame_elo;

  if (homeElo === null || awayElo === null) return false;

  const eloDiff = Math.abs(homeElo - awayElo);
  return eloDiff <= 50;
};

/**
 * Check if game is a mismatch (ELO difference ≥ 200)
 */
const isMismatch = (game) => {
  const homeElo = game.home_pregame_elo;
  const awayElo = game.away_pregame_elo;

  if (homeElo === null || awayElo === null) return false;

  const eloDiff = Math.abs(homeElo - awayElo);
  return eloDiff >= 200;
};

/**
 * Check if game is a barn burner (excitement index ≥ 8.0)
 */
const isBarnBurner = (game) => {
  const excitement = game.excitement_index;
  return excitement !== null && excitement >= 8.0;
};

/**
 * Check if game is at a neutral site
 */
const isNeutralSite = (game) => {
  return game.neutral_site === 1;
};

/**
 * Check if game meets excitement index minimum threshold
 */
const meetsExcitementIndexMin = (game, minExcitement) => {
  const excitement = game.excitement_index;
  return excitement !== null && excitement >= minExcitement;
};

/**
 * Check if game meets excitement index maximum threshold
 */
const meetsExcitementIndexMax = (game, maxExcitement) => {
  const excitement = game.excitement_index;
  return excitement !== null && excitement <= maxExcitement;
};

/**
 * Check if game meets ELO difference minimum threshold
 */
const meetsEloDifferenceMin = (game, minEloDiff) => {
  const homeElo = game.home_pregame_elo;
  const awayElo = game.away_pregame_elo;

  if (homeElo === null || awayElo === null) return false;

  const eloDiff = Math.abs(homeElo - awayElo);
  return eloDiff >= minEloDiff;
};

/**
 * Check if game meets ELO difference maximum threshold
 */
const meetsEloDifferenceMax = (game, maxEloDiff) => {
  const homeElo = game.home_pregame_elo;
  const awayElo = game.away_pregame_elo;

  if (homeElo === null || awayElo === null) return false;

  const eloDiff = Math.abs(homeElo - awayElo);
  return eloDiff <= maxEloDiff;
};

/**
 * Get filter summary for display
 */
// Helper functions for getFilterSummary
const getSpreadFilterText = (filters) => {
  if ((filters.spreadMin === null || filters.spreadMin === undefined) &&
      (filters.spreadMax === null || filters.spreadMax === undefined)) return null;
  const spreadRange = [];
  if (filters.spreadMin !== null && filters.spreadMin !== undefined) spreadRange.push(`≥${filters.spreadMin}`);
  if (filters.spreadMax !== null && filters.spreadMax !== undefined) spreadRange.push(`≤${filters.spreadMax}`);
  return `Spread: ${spreadRange.join(' - ')}`;
};

const getTotalFilterText = (filters) => {
  if ((filters.overUnderMin === null || filters.overUnderMin === undefined) &&
      (filters.overUnderMax === null || filters.overUnderMax === undefined)) return null;
  const totalRange = [];
  if (filters.overUnderMin !== null && filters.overUnderMin !== undefined) totalRange.push(`≥${filters.overUnderMin}`);
  if (filters.overUnderMax !== null && filters.overUnderMax !== undefined) totalRange.push(`≤${filters.overUnderMax}`);
  return `Total: ${totalRange.join(' - ')}`;
};

const getBasicFilterTexts = (filters) => {
  const texts = [];

  if (filters.teamSearch && filters.teamSearch.trim() !== '') {
    texts.push(`Search: "${filters.teamSearch}"`);
  }
  if (filters.top25) texts.push('Top 25');
  if (filters.rankedMatchups) texts.push('Ranked Matchups');
  if (filters.fbs) texts.push('FBS');
  if (filters.fcs) texts.push('FCS');
  if (filters.homeUnderdogs) texts.push('Home Underdogs');
  if (filters.atsLockAvailable) texts.push('ATS Lock Available');
  if (filters.overUnderLockAvailable) texts.push('O/U Lock Available');
  if (filters.closeMatchups) texts.push('Close Matchups');
  if (filters.mismatches) texts.push('Mismatches');
  if (filters.barnBurners) texts.push('Barn Burners Live Now');
  if (filters.neutralSite) texts.push('Neutral Site');

  return texts;
};

const getConferenceFilterTexts = (filters) => {
  const texts = [];

  if (filters.conferenceType && filters.conferenceType !== 'all') {
    const typeLabel = filters.conferenceType === 'conference' ? 'Conference Games' : 'Non-Conference Games';
    texts.push(typeLabel);
  }
  if (filters.conferences.length > 0) {
    texts.push(`${filters.conferences.length} Conference(s)`);
  }

  return texts;
};

const getRangeFilterTexts = (filters) => {
  const texts = [];

  const spreadText = getSpreadFilterText(filters);
  if (spreadText) texts.push(spreadText);

  const totalText = getTotalFilterText(filters);
  if (totalText) texts.push(totalText);

  return texts;
};

export const getFilterSummary = (filters) => {
  const activeFilters = [
    ...getBasicFilterTexts(filters),
    ...getConferenceFilterTexts(filters),
    ...getRangeFilterTexts(filters)
  ];

  return activeFilters;
};

/**
 * Get preset filter names and descriptions
 */
export const getPresetFilters = () => {
  return [
    { id: 'top25-only', name: 'Top 25 Only', description: 'Games with at least one ranked team' },
    { id: 'ranked-matchups', name: 'Ranked Matchups', description: 'Games where both teams are ranked' },
    { id: 'fbs-only', name: 'FBS Only', description: 'Games involving FBS teams' },
    { id: 'home-underdogs', name: 'Home Underdogs', description: 'Games where home team is underdog' },
    { id: 'neutral-site', name: 'Neutral Site', description: 'Games at neutral venues' },
    { id: 'small-spread', name: 'Small Spread (≤3)', description: 'Games with spread of 3 or less' },
    { id: 'large-spread', name: 'Large Spread (≥15)', description: 'Games with spread of 15 or more' },
    { id: 'small-total', name: 'Small Total (≤35)', description: 'Games with total of 35 or less' },
    { id: 'large-total', name: 'Large Total (≥70)', description: 'Games with total of 70 or more' }
  ];
};

// Export all functions for testing
export {
  passesTeamFilters,
  passesConferenceFilters,
  passesBettingFilters,
  passesGameTypeFilters,
  passesAdvancedFilters,
  getBasicFilterTexts,
  getConferenceFilterTexts,
  getRangeFilterTexts
};
